import React from 'react';
import { useParams, useLocation, useNavigate, type Location } from 'react-router-dom';

/**
 * Bruecke von react-router v5 auf v7.
 *
 * In v5 hat `<Route component={X} />` die Props `match`, `location` und `history`
 * in die Komponente injiziert; mehrere Container in diesem Projekt lesen daraus
 * `ownProps.match.params.uuid`. In v7 gibt es diese Injektion nicht mehr — die
 * Werte kommen ueber Hooks.
 *
 * `withRouteParams` stellt die alte Prop-Form wieder her, sodass die bestehenden
 * Container unveraendert bleiben koennen.
 *
 * Verwendung:
 *   <Route path="/Ziel/:uuid" element={withRouteParams(TargetDetailContainer)} />
 */
export interface LegacyMatch {
  params: Record<string, string | undefined>;
  url: string;
  path: string;
  isExact: boolean;
}

export interface LegacyRouteProps {
  match: LegacyMatch;
  location: Location;
  history: LegacyHistory;
}

/**
 * Der Teil von v5's `history`, den dieses Projekt benutzt.
 *
 * Ohne Bruecke ist `ownProps.history` schlicht undefiniert - ein
 * `history.push` laeuft dann in einen TypeError.
 */
export interface LegacyHistory {
  push: (pfad: string) => void;
  replace: (pfad: string) => void;
  goBack: () => void;
}

export function withRouteParams(
  Component: React.ComponentType<any>,
  extraProps: Record<string, unknown> = {},
): React.ReactElement {
  const Bridge: React.FC = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const match: LegacyMatch = {
      params,
      url: location.pathname,
      path: location.pathname,
      isExact: true,
    };

    const history: LegacyHistory = {
      push: (pfad) => navigate(pfad),
      replace: (pfad) => navigate(pfad, { replace: true }),
      goBack: () => navigate(-1),
    };

    return (
      <Component match={match} location={location} history={history} {...extraProps} />
    );
  };

  return <Bridge />;
}

export default withRouteParams;
